import React from 'react';
import { styles } from '../styles/clientManagementStyles';
import { shallowEqualIgnoringFunctions } from '../utils/memoCompare';
import { colors as theme } from '../theme';

const ClientDetailHeader = ({
  selectedClient,
  setSelectedClient,
  setDeleteDialog,
  clientGlp1,
  activeTab,
  setActiveTab,
  loadProgramHistory,
  setShowAssignPanel,
  openMessagesTab,
  clientUnreadMessageCounts,
}) => {
  return (
    <>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <button onClick={() => setSelectedClient(null)} style={styles.backBtn}>
          ← Back to Clients
        </button>
        <button
          onClick={() => setDeleteDialog({ isOpen: true, client: selectedClient })}
          style={styles.deleteClientBtn}
        >
          Delete Client
        </button>
      </div>

      {/* Client Info Card */}
      <div style={styles.clientDetailCard}>
        <div style={styles.clientHeader}>
          <div style={styles.avatar}>
            {selectedClient.full_name.charAt(0).toUpperCase()}
          </div>
          <div>
            <h1 style={styles.clientName}>{selectedClient.full_name}</h1>
            <p style={styles.clientEmail}>{selectedClient.email}</p>
          </div>
        </div>

        {/* Full Onboarding Info Grid */}
        <div style={styles.infoGrid}>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Age</span>
            <span style={styles.infoValue}>{selectedClient.age || '-'}</span>
          </div>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Sex</span>
            <span style={styles.infoValue}>{selectedClient.sex || '-'}</span>
          </div>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Height</span>
            <span style={styles.infoValue}>{selectedClient.height ? `${selectedClient.height} cm` : '-'}</span>
          </div>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Weight</span>
            <span style={styles.infoValue}>{selectedClient.weight ? `${selectedClient.weight} kg` : '-'}</span>
          </div>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Goal</span>
            <span style={styles.infoValue}>
              {selectedClient.primary_goal?.replace(/_/g, ' ') || '-'}
            </span>
          </div>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Training Location</span>
            <span style={styles.infoValue}>
              {selectedClient.training_location || '-'}
            </span>
          </div>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Experience Level</span>
            <span style={styles.infoValue}>
              {selectedClient.experience_level || '-'}
            </span>
          </div>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Exercise Frequency</span>
            <span style={styles.infoValue}>
              {selectedClient.exercise_frequency ? `${selectedClient.exercise_frequency} days/week` : '-'}
            </span>
          </div>
          {selectedClient.phone_number && (
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Phone</span>
              <span style={styles.infoValue}>{selectedClient.phone_number}</span>
            </div>
          )}
          {selectedClient.coach_code && (
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Coach Code</span>
              <span style={styles.infoValue}>{selectedClient.coach_code}</span>
            </div>
          )}
        </div>

        {/* Home Equipment (if training at home) */}
        {selectedClient.training_location === 'home' && selectedClient.home_equipment && selectedClient.home_equipment.length > 0 && (
          <div style={styles.equipmentBox}>
            <span style={styles.equipmentLabel}>Home Equipment</span>
            <div style={styles.equipmentTags}>
              {selectedClient.home_equipment.map((item, idx) => (
                <span key={idx} style={styles.equipmentTag}>
                  {item.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Injuries Section */}
        {(selectedClient.has_injuries || selectedClient.injuries) && (
          <div style={styles.injuryBox}>
            <span style={styles.injuryLabel}>⚠️ Injuries/Conditions</span>
            <p style={styles.injuryText}>
              {selectedClient.injuries || 'Yes (details not specified)'}
            </p>
          </div>
        )}

        {/* GLP-1 Medication Alert (from Cipher Nutrition) */}
        {clientGlp1 && (
          <div style={styles.glp1Box}>
            <span style={styles.glp1Label}>💊 GLP-1 Medication · Cipher Nutrition</span>
            <p style={styles.glp1Text}>
              Client is on <strong style={{ color: theme.gold }}>{clientGlp1.medication}</strong>{' '}
              ({(clientGlp1.dose_stage || '').replace(/_/g, ' ')} phase
              {clientGlp1.months_on_med ? `, ${clientGlp1.months_on_med.replace(/_/g, ' ')} on medication` : ''}).
              Consider lower-intensity sessions and sufficient recovery time.
              {clientGlp1.challenges?.length > 0 && (
                <> Reported challenges: {clientGlp1.challenges.map(c => c.replace(/_/g, ' ')).join(', ')}.</>
              )}
            </p>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={styles.tabContainer}>
        <button
          onClick={() => setActiveTab('performance')}
          style={{
            ...styles.tabButton,
            ...(activeTab === 'performance' ? styles.tabButtonActive : {}),
          }}
        >
          Performance
        </button>
        <button
          onClick={() => { setActiveTab('programs'); loadProgramHistory(selectedClient.user_id); setShowAssignPanel(false); }}
          style={{
            ...styles.tabButton,
            ...(activeTab === 'programs' ? styles.tabButtonActive : {}),
          }}
        >
          Programs
        </button>
        <button
          onClick={() => setActiveTab('checkins')}
          style={{
            ...styles.tabButton,
            ...(activeTab === 'checkins' ? styles.tabButtonActive : {}),
          }}
        >
          Check-Ins
        </button>
        <button
          onClick={() => setActiveTab('nutrition')}
          style={{
            ...styles.tabButton,
            ...(activeTab === 'nutrition' ? styles.tabButtonActive : {}),
          }}
        >
          Nutrition
        </button>
        <button
          onClick={openMessagesTab}
          style={{
            ...styles.tabButton,
            ...(activeTab === 'messages' ? styles.tabButtonActive : {}),
            position: 'relative',
          }}
        >
          Messages
          {clientUnreadMessageCounts[selectedClient.user_id] > 0 && (
            <span style={{
              position: 'absolute', top: '2px', right: '2px',
              backgroundColor: theme.danger, color: theme.text,
              borderRadius: '50%', width: '16px', height: '16px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '11px', fontWeight: 700,
            }}>
              {clientUnreadMessageCounts[selectedClient.user_id]}
            </span>
          )}
        </button>
      </div>
    </>
  );
};

export default React.memo(ClientDetailHeader, shallowEqualIgnoringFunctions);
